import { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, Clock3 } from "lucide-react";

import { appCheckFetch } from "@/lib/firebase";
import { PODCAST_CONFIG } from "../podcast-config";

interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  publishedAt: string;
  thumbnailUrl: string;
  durationSeconds?: number;
  url: string;
}

interface YouTubePlaylistResponse {
  videos?: YouTubeVideo[];
  playlistUrl?: string;
}

function parseYouTubeFeed(xml: string): YouTubeVideo[] {
  const document = new DOMParser().parseFromString(xml, "application/xml");
  if (document.querySelector("parsererror")) throw new Error("El feed de YouTube no es válido.");

  return Array.from(document.querySelectorAll("entry")).flatMap((entry) => {
    const id = entry.getElementsByTagName("yt:videoId")[0]?.textContent?.trim();
    const title = entry.querySelector("title")?.textContent?.trim();
    const publishedAt = entry.querySelector("published")?.textContent?.trim();
    const description =
      entry.getElementsByTagName("media:description")[0]?.textContent?.trim() ?? "";
    const thumbnailUrl = entry.getElementsByTagName("media:thumbnail")[0]?.getAttribute("url");
    if (!id || !title || !publishedAt || !thumbnailUrl) return [];

    return [
      {
        id,
        title,
        description,
        publishedAt,
        thumbnailUrl,
        url: `https://www.youtube.com/watch?v=${encodeURIComponent(id)}&list=PLNDpF0fbDWhk`,
      },
    ];
  });
}

type YouTubeState =
  | { status: "loading"; videos: YouTubeVideo[] }
  | { status: "ready"; videos: YouTubeVideo[] }
  | { status: "error"; videos: YouTubeVideo[] };

const youtubePlatform = PODCAST_CONFIG.platforms.find((platform) => platform.kind === "youtube");

function YouTubeLogo({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 68 48"
      className={className}
      role="img"
      aria-label="YouTube"
    >
      <rect width="68" height="48" rx="11" fill="#FF0033" />
      <path d="M28 14.5 50 24 28 33.5Z" fill="white" />
    </svg>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatDuration(seconds?: number) {
  if (!seconds || seconds < 1) return undefined;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
    : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function VideoMeta({ video, inverse = false }: { video: YouTubeVideo; inverse?: boolean }) {
  const duration = formatDuration(video.durationSeconds);
  const className = inverse ? "text-white/55" : "text-black/45";

  return (
    <span className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-xs ${className}`}>
      <span className="inline-flex items-center gap-1.5">
        <CalendarDays className="h-3.5 w-3.5" />
        {formatDate(video.publishedAt)}
      </span>
      {duration && (
        <span className="inline-flex items-center gap-1.5">
          <Clock3 className="h-3.5 w-3.5" />
          {duration}
        </span>
      )}
    </span>
  );
}

export function PodcastYouTubeSection() {
  const [state, setState] = useState<YouTubeState>({ status: "loading", videos: [] });

  useEffect(() => {
    const controller = new AbortController();

    async function loadVideos() {
      try {
        const request = import.meta.env.DEV ? fetch : appCheckFetch;
        const response = await request("/api/youtube-playlist", {
          signal: controller.signal,
          headers: {
            Accept: import.meta.env.DEV
              ? "application/atom+xml, application/xml, text/xml"
              : "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("YouTube no respondió correctamente.");
        }

        if (import.meta.env.DEV) {
          setState({ status: "ready", videos: parseYouTubeFeed(await response.text()) });
          return;
        }

        const data = (await response.json().catch(() => null)) as YouTubePlaylistResponse | null;
        if (!Array.isArray(data?.videos)) throw new Error("YouTube no respondió correctamente.");
        setState({ status: "ready", videos: data.videos });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState({ status: "error", videos: [] });
      }
    }

    void loadVideos();
    return () => controller.abort();
  }, []);

  const [featured, previous, ...remaining] = state.videos;

  return (
    <section
      aria-labelledby="youtube-videos-title"
      className="border-t border-black/10 bg-[#f6f6f3]"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-18">
        <div className="flex flex-col justify-between gap-5 border-b border-black pb-5 sm:flex-row sm:items-end">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
              <YouTubeLogo className="h-4 w-auto" />
              Canal de YouTube
            </p>
            <h2
              id="youtube-videos-title"
              className="mt-2 text-3xl font-semibold tracking-[-0.05em] sm:text-4xl"
            >
              Mira Socrattica en video. 
            </h2>
          </div>
          {youtubePlatform?.url && (
            <a
              href={youtubePlatform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-semibold underline decoration-black/30 underline-offset-4 hover:decoration-black"
            >
              Ver la playlist completa
              <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
        </div>

        {state.status === "loading" && (
          <div className="mt-8 grid gap-5 lg:grid-cols-[2fr_1fr]" role="status">
            <div className="animate-pulse bg-black/10">
              <div className="aspect-video" />
              <div className="h-40 border-t border-black/5" />
            </div>
            <div className="animate-pulse bg-black/10">
              <div className="aspect-video" />
              <div className="h-32 border-t border-black/5" />
            </div>
            <span className="sr-only">Cargando videos de YouTube</span>
          </div>
        )}

        {state.status === "error" && (
          <div className="mt-8 flex flex-col justify-between gap-5 border border-black/15 bg-white p-6 sm:flex-row sm:items-center">
            <div>
              <p className="text-sm font-semibold">Los videos no están disponibles ahora.</p>
              <p className="mt-1 text-xs leading-5 text-black/50">
                Puedes continuar directamente en la playlist oficial de Socrattica.
              </p>
            </div>
            {youtubePlatform?.url && (
              <a
                href={youtubePlatform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-black px-4 text-xs font-semibold text-white"
              >
                Abrir YouTube
                <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </div>
        )}

        {state.status === "ready" && !featured && (
          <p className="py-12 text-sm text-black/50">
            Todavía no hay videos publicados en esta playlist.
          </p>
        )}

        {featured && (
          <>
            <div
              className={`mt-8 grid items-stretch gap-4 ${
                previous
                  ? "lg:aspect-[8/3] lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]"
                  : ""
              }`}
            >
              <a
                href={featured.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group grid w-full min-w-0 max-w-full overflow-hidden bg-black text-white md:aspect-video md:grid-cols-[52%_48%] lg:h-full lg:aspect-auto"
              >
                <div className="relative aspect-video overflow-hidden bg-black md:aspect-auto md:h-full">
                  <img
                    src={featured.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <YouTubeLogo className="absolute left-1/2 top-1/2 h-12 w-auto -translate-x-1/2 -translate-y-1/2 shadow-xl transition group-hover:scale-105 sm:h-14" />
                </div>
                <div className="flex min-w-0 flex-col justify-between overflow-hidden p-5 sm:p-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/65">
                      Video más reciente
                    </p>
                    <h3 className="mt-3 line-clamp-3 text-lg font-semibold leading-tight tracking-[-0.04em] xl:text-xl">
                      {featured.title}
                    </h3>
                    {featured.description && (
                      <p
                        className="mt-4 overflow-hidden text-sm leading-6 text-white/60"
                        style={{
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 6,
                          maxHeight: "9rem",
                        }}
                      >
                        {featured.description}
                      </p>
                    )}
                  </div>
                  <span className="mt-8 flex items-end justify-between gap-4">
                    <VideoMeta video={featured} inverse />
                    <ArrowUpRight className="h-5 w-5 shrink-0 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </span>
                </div>
              </a>

              {previous && (
                <a
                  href={previous.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full min-w-0 flex-col overflow-hidden border border-black/15 bg-white"
                >
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <img
                      src={previous.thumbnailUrl}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    />
                    <YouTubeLogo className="absolute bottom-3 left-3 h-9 w-auto shadow-lg" />
                  </div>
                  <span className="flex flex-1 flex-col justify-between p-5 sm:p-6">
                    <span>
                      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-black/50">
                        Episodio anterior
                      </span>
                      <span className="mt-3 block text-xl font-semibold leading-snug tracking-[-0.03em]">
                        {previous.title}
                      </span>
                      {previous.description && (
                        <span
                          className="mt-3 overflow-hidden text-xs leading-5 text-black/50"
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                            maxHeight: "3.75rem",
                          }}
                        >
                          {previous.description}
                        </span>
                      )}
                    </span>
                    <span className="mt-6 flex items-end justify-between gap-4">
                      <VideoMeta video={previous} />
                      <ArrowUpRight className="h-5 w-5 shrink-0 text-black/40 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-black" />
                    </span>
                  </span>
                </a>
              )}
            </div>

            {remaining.length > 0 && (
              <div className="mt-6 grid gap-px overflow-hidden border border-black/15 bg-black/15 sm:grid-cols-2 lg:grid-cols-3">
                {remaining.map((video) => (
                  <a
                    key={video.id}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col bg-white"
                  >
                    <div className="relative aspect-video overflow-hidden bg-black">
                      <img
                        src={video.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                      />
                      <YouTubeLogo className="absolute bottom-3 left-3 h-8 w-auto shadow-lg" />
                    </div>
                    <span className="flex flex-1 flex-col p-5">
                      <VideoMeta video={video} />
                      <span className="mt-3 flex items-start justify-between gap-4">
                        <span className="font-semibold leading-snug">{video.title}</span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-black/40 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-black" />
                      </span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
